import { Capacitor } from "@capacitor/core";
import { authErrorMessage, SEND_CODE_FAILED } from "../../services/auth";
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { AllCountryCodes } from "../../utils/AllCountryCodes";
import {
  IonPage,
  IonContent,
  IonCardContent,
  IonLabel,
  IonCheckbox,
  IonButton,
  IonText,
  useIonRouter,
  IonIcon,
  IonSpinner,
} from "@ionic/react";
import "./LoginPage.css";

import { alertCircleOutline } from "ionicons/icons";
import useAuth from "../../hooks/useAuth";
import { Preferences } from "@capacitor/preferences";
import { NativeBiometric } from "capacitor-native-biometric";
import { countryCodes } from "../../utils/constants";
import { GUEST_ROUTE, enterGuestMode } from "../../utils/guestSession";
import ActionButtons from "../ui/popups/ActionButtons";
import Loader from "../helpers/Loader";
import { Network } from "@capacitor/network";
import SocialButtons from "./SocialComponents";
import SelectionModal from "../ui/popups/SelectionModal";
import { useTermsAgreement } from "../../hooks/useTermsAgreement";
import TermsAgreementModal from "../ui/TermsAgreementModal";
import { getCountryDetails } from "../home/EnvironmentalDataManager";
import { useLocation } from "react-router-dom";
import analytics from "../../services/firebaseAnalytics";
import { useAnalyticsClick } from "../../hooks/useAnalyticsClick";
import { useNetworkStatus } from "../../hooks/useNetworkStatus";
import { isSSOCancelError, friendlySSOError } from "../../utils/ssoErrors";
import DesktopLoginLayout from "./LoginDesktop";
import { ensureGoogleInitialized } from "../../services/auth";
import { takeReturnTo } from "../../routes/returnTo";

// Helper functions
const findCountryCodeMatch = (input: string) => {
  const sortedCodes = countryCodes
    .map((c) => c.code)
    .sort((a, b) => b.length - a.length);

  for (const code of sortedCodes) {
    if (input.startsWith(code)) {
      return {
        countryCode: code,
        localNumber: input.slice(code.length),
      };
    }
  }
  return null;
};

const isEmailInput = (input: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.trim());
};

// FIXED: More strict phone detection since we default to email mode
const isPhoneInput = (input: string) => {
  const trimmed = input.trim();

  // Definitely not phone if contains @ or clear email patterns
  if (trimmed.includes("@")) return false;
  if (/^[a-zA-Z][a-zA-Z0-9]*\.[a-zA-Z]/.test(trimmed)) return false;
  if (/^[a-zA-Z][a-zA-Z0-9._-]+$/.test(trimmed) && /[._-]/.test(trimmed))
    return false;

  // Strong phone indicators
  if (trimmed.startsWith("+")) return true;

  // Must be mostly or all digits to be considered phone
  if (/^\d+$/.test(trimmed) && trimmed.length >= 4) return true;

  // Phone patterns with formatting, but must be mostly digits
  const phonePattern = /^[\d\s\-()+]*$/;
  const digitCount = (trimmed.match(/\d/g) || []).length;
  const totalLength = trimmed.length;

  // At least 70% digits and follows phone pattern
  return (
    phonePattern.test(trimmed) &&
    digitCount >= 1 &&
    digitCount / totalLength >= 0.1
  );
};

// FIXED: More relaxed email detection since we default to email mode
const isLikelyEmail = (input: string) => {
  const trimmed = input.trim();

  // Definitely email if contains @
  if (trimmed.includes("@")) return true;

  // Clear email domain patterns
  if (/^[a-zA-Z][a-zA-Z0-9]*\.[a-zA-Z][a-zA-Z0-9]*/.test(trimmed)) return true;

  // Since we default to email, we can be less aggressive about detection
  // Only switch away from email if it's clearly NOT an email
  return false;
};

export interface Address {
  latitude: number;
  longitude: number;
  countryCode: string;
  countryName: string;
  postalCode: string;
  administrativeArea: string;
  subAdministrativeArea: string;
  locality: string;
  subLocality: string;
  thoroughfare: string;
  subThoroughfare: string;
  areasOfInterest: string[];
  addressLines: string[];
}

const Register: React.FC = () => {
  // Legacy multiple phone numbers support (from first file)
  const [phoneNumbers, setPhoneNumbers] = useState([""]);
  const isOnline = useNetworkStatus();
  // Modern single input approach (from second file - better UX)
  const [isEmailMode, setIsEmailMode] = useState(true); // FIXED: Default to true (email mode)
  const [inputValue, setInputValue] = useState("");
  const [countryCode, setCountrycode] = useState("");
  const mappedCountryCodes = Object.entries(AllCountryCodes).map(
    ([code, info]) => ({
      id: code,
      label: `${code} ${info.name}`,
      selected: countryCode === code,
      flag: info.flag || "",
    }),
  );
  // Common state variables
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [modalContentType, setModalContentType] = useState<
    "terms" | "privacy" | null | undefined
  >("terms");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Please wait...");
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  // check query param to set register or login mode
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialRegisterMode = queryParams.get("register") !== "false";
  const [registerMode, setRegisterMode] = useState(initialRegisterMode);
  const [userLocation, setUserLocation] = useState<
    {
      code: string;
      country: string;
      dateFormat: string;
      timeZone: string;
    }[]
  >([]);

  const [codeModal, setCodeModal] = useState(false);
  const trackClick = useAnalyticsClick();
  const [showBiometricAlert, setShowBiometricAlert] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [showTermsRequiredModal, setShowTermsRequiredModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<null | (() => void)>(null);

  const {
    signInWithSocial,
    isAuthenticated,
    isDesktop,
    registerWithPhone,
    registerWithEmail,
  } = useAuth();

  // Terms agreement hook
  const { hasAgreed, agreeToTerms, resetTermsAgreement } = useTermsAgreement();

  const router = useIonRouter();
  useEffect(() => {
    const getUserLocation = async () => {
      const Location: any = await getCountryDetails();

      await Preferences.set({
        key: "userLocation",
        value: JSON.stringify(Location),
      });
      setUserLocation(Location);
    };
    // getUserLocation(); //Removing the country code selection code
  }, []);

  // FIXED: Smart automatic input mode detection - defaults to email
  useEffect(() => {
    const trimmed = inputValue.trim();

    // Strong phone indicators - switch to phone mode
    if (trimmed.startsWith("+") || /^\d{3,}$/.test(trimmed)) {
      setIsEmailMode(false);
    }
    // Strong email indicators - stay in or switch to email mode
    else if (
      trimmed.includes("@") ||
      /^[a-zA-Z][a-zA-Z0-9]*\.[a-zA-Z]/.test(trimmed)
    ) {
      setIsEmailMode(true);
    }
    // Moderate phone indicators
    else if (isPhoneInput(trimmed)) {
      setIsEmailMode(false);
    }
    // Moderate email indicators or default behavior
    else if (isLikelyEmail(trimmed)) {
      setIsEmailMode(true);
    }
    // For ambiguous cases, use context clues
    else {
      // If it's mostly numbers or starts with digits, switch to phone
      if (
        /^\d/.test(trimmed) &&
        (trimmed.match(/\d/g) || []).length >
          (trimmed.match(/[a-zA-Z]/g) || []).length
      ) {
        setIsEmailMode(false);
      }
      // If it starts with letters or mixed content, lean towards email (default)
      else if (/^[a-zA-Z]/.test(trimmed) || !/^\d+$/.test(trimmed)) {
        setIsEmailMode(true);
      }
      // Keep current mode for very ambiguous input
    }
  }, [inputValue]);

  // Track if country code was set manually
  const [countryCodeManuallySet, setCountryCodeManuallySet] = useState(false);

  useEffect(() => {
    // Only set country code when switching to phone mode
    if (!isEmailMode) {
      if (!countryCodeManuallySet) {
        if (userLocation.length > 0) {
          setCountrycode(userLocation[0]?.code || "+1");
        } else {
          setCountrycode("+1");
        }
      }
    }
  }, [isEmailMode, userLocation, countryCodeManuallySet]);

  // Error handling effect
  useEffect(() => {
    if (showError) {
      const timer = setTimeout(() => {
        setShowError(false);
        setErrorMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showError]);

  // Initialization effect
  useEffect(() => {
    if (isAuthenticated) {
      handleAuthenticatedUser();
      return;
    }
    // Initialize with default email mode
    setInputValue("");
    setPhoneNumbers([""]);
    setIsEmailMode(true); // Default to email mode
    setCountrycode("+1");
  }, [isAuthenticated]);

  // Pre-initialize Google login on mount to keep popup tied to user gesture later
  useEffect(() => {
    ensureGoogleInitialized().catch((err) => {
      console.error("Google init failed:", err);
    });
  }, []);
  const APPLE_STORE_URL = "https://apps.apple.com/us/app/arc-connect/id6745964099";
  const GOOGLE_PLAY_STORE_URL =
    "https://play.google.com/store/apps/details?id=com.abmrc.arcconnectios";
  const openExternal = (url: string) => {
    // Use anchor element to ensure compatibility with iOS Safari and popup blockers
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  useEffect(() => {
    // Function to detect OS and redirect, but only once per hour
    const detectOSAndRedirect = () => {
      // Only for the WEB patient app opened in a phone browser: point the user at the store.
      // Inside the installed app (Capacitor native) the WebView's user agent also says
      // Android/iPhone, and this sent the installed Android app straight to the Play Store on
      // the sign-in screen (iOS WKWebView ignored the target=_blank link, so it looked fine).
      if (Capacitor.isNativePlatform()) return;
      const lastRedirect = localStorage.getItem("lastAppStoreRedirect");
      const now = Date.now();
      if (lastRedirect && now - Number(lastRedirect) < 60 * 60 * 1000) {
        // Already redirected within the last hour
        return;
      }

      const userAgent = navigator.userAgent;
      // iOS detection
      if (/iPad|iPhone|iPod/.test(userAgent) && !('MSStream' in window)) {
        openExternal(APPLE_STORE_URL);
        localStorage.setItem("lastAppStoreRedirect", now.toString());
        return;
      }

      // Android detection
      if (/android/i.test(userAgent)) {
        openExternal(GOOGLE_PLAY_STORE_URL);
        localStorage.setItem("lastAppStoreRedirect", now.toString());
        return;
      }

      // For desktop or other platforms, stay on the page
    };

    const redirectTimer = setTimeout(() => {
      detectOSAndRedirect();
    }, 500);

    return () => clearTimeout(redirectTimer);
  }, []);
  const handleAuthenticatedUser = async () => {
    try {
      const { value } = await Preferences.get({ key: "biometric" });
      if (value === "true") {
        setShowBiometricAlert(true);
        // handleBiometricLogin();
      }
    } catch (error) {
      console.error("Error checking biometric status:", error);
    }
  };

  const formatByPattern = (countryCode: string, digits: string) => {
    if (digits.length === 0) {
      return "";
    }
    const country =
      AllCountryCodes[countryCode as keyof typeof AllCountryCodes];
    if (!country) return digits;

    // Special handling for US/Canada - return only formatted number without country code
    if (countryCode === "+1") {
      if (digits.length <= 3) {
        return `(${digits}`;
      } else if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
          6,
        )}`;
      }
    }

    // Parse the pattern to understand how to format
    const pattern = country.pattern;
    const digitPattern = pattern.replace(countryCode, "").trim();

    // Parse pattern to extract formatting groups
    const formatGroups: {
      type: "digits" | "separator";
      count?: number;
      char?: string;
    }[] = [];
    let currentGroup = "";
    for (let i = 0; i < digitPattern.length; i++) {
      const char = digitPattern[i];
      if (char === "#") {
        currentGroup += "#";
      } else if (char === " " || char === "-" || char === "(" || char === ")") {
        if (currentGroup) {
          formatGroups.push({ type: "digits", count: currentGroup.length });
          currentGroup = "";
        }
        if (char !== " ") {
          formatGroups.push({ type: "separator", char: char });
        } else {
          formatGroups.push({ type: "separator", char: " " });
        }
      }
    }
    if (currentGroup) {
      formatGroups.push({ type: "digits", count: currentGroup.length });
    }

    // Apply formatting based on parsed groups - return only formatted number
    let result = "";
    let digitPos = 0;
    let isFirstGroup = true;

    for (const group of formatGroups) {
      if (group.type === "digits" && typeof group.count === "number") {
        const groupDigits = digits.slice(digitPos, digitPos + group.count);
        if (groupDigits.length > 0) {
          // Don't add space before first group, but add space before subsequent digit groups
          if (
            !isFirstGroup &&
            result &&
            !result.endsWith(" ") &&
            !result.endsWith("(") &&
            !result.endsWith("-")
          ) {
            result += " ";
          }
          result += groupDigits;
          digitPos += group.count;
          isFirstGroup = false;
        }
        if (digitPos >= digits.length) break;
      } else if (group.type === "separator" && digitPos < digits.length) {
        const remainingDigits = digits.slice(digitPos);
        if (remainingDigits.length > 0 && group.char) {
          result += group.char;
        }
      }
    }

    return result;
  };

  const formatPhoneNumber = (value: string, CountryCode: string) => {
    const cleaned = value.replace(/[^\d+]/g, "");
    return formatByPattern(CountryCode, cleaned);
  };

  // FIXED: Enhanced input handling with email-first approach
  const handleInputChange = (value: string) => {
    const trimmedInput = value.trim();

    if (!trimmedInput) {
      setInputValue("");
      setEmail("");
      setPhoneNumbers([""]);
      setIsEmailMode(true);
      return;
    }

    setInputValue(trimmedInput);

    if (
      isPhoneInput(trimmedInput) ||
      /^\d+$/.test(trimmedInput) ||
      trimmedInput.startsWith("+")
    ) {
      // Set country code based on user location or default to +1
      if (!countryCode && userLocation.length > 0) {
        setCountrycode(userLocation[0]?.code || "+1");
      } else if (!countryCode) {
        setCountrycode("+1");
      }

      // Handle as phone number
      if (!countryCode && userLocation.length > 0) {
        setCountrycode(userLocation[0]?.code || "+1");
      } else if (!countryCode) {
        setCountrycode("+1");
      }

      // Format phone number if it's clearly a phone number
      if (
        countryCode &&
        (isPhoneInput(trimmedInput) || /^\d+$/.test(trimmedInput))
      ) {
        const cleanedInput = trimmedInput.replace(/[^\d]/g, "");
        const formattedPhone = formatPhoneNumber(cleanedInput, countryCode);
        setInputValue(formattedPhone);
        setPhoneNumbers([formattedPhone]);
      } else {
        // Store raw phone input
        setPhoneNumbers([trimmedInput]);
      }
    }
    // Handle as email (default behavior)
    else {
      const emailValue = trimmedInput.toLowerCase();
      setEmail(emailValue);
      setPhoneNumbers([emailValue]);
    }
  };

  const isInputValid = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return false;

    if (isEmailMode) {
      // For email, allow reasonable partial emails or valid complete emails
      return isEmailInput(trimmed);
    } else {
      // For phone, check if it has reasonable length and contains enough digits
      const cleanedPhone = trimmed.replace(/[^\d]/g, "");
      return cleanedPhone.length >= 4 && cleanedPhone.length <= 15;
    }
  };

  // Registration handling - enhanced with network checking
  const handleRegister = async () => {
    // ⚠️ Do NOT prepend countryCode here. registerWithPhone passes this straight to
    // toE164 ALONG WITH countryCode, so composing it here applied the code TWICE and
    // produced numbers like +91917996162528 — shape-legal E.164 that silently created a
    // duplicate patient profile (D15b). toE164 owns composition; this owns the digits.
    const currentValue = isEmailMode
      ? email || inputValue
      : inputValue.replace(/[^\d]/g, ""); // national digits only — toE164 adds the code
    let sanitizedValue: any = currentValue;

    if (!isEmailMode) {
      sanitizedValue = currentValue.replace(/[^+\d]/g, "");
    }

    // Validation
    if (isEmailMode) {
      if (!currentValue || !isEmailInput(currentValue)) {
        setErrorMessage("Please enter a valid email address");
        setShowError(true);
        return;
      }
    } else {
      if (
        !currentValue ||
        currentValue === "+" ||
        currentValue === countryCode
      ) {
        setErrorMessage("Please enter a valid phone number");
        setShowError(true);
        return;
      }
    }

    if (!hasAgreed && registerMode) {
      setErrorMessage("You must agree to the Terms and Conditions");
      setShowError(true);
      return;
    }

    try {
      setLoading(true);
      setLoadingMessage("Processing...");
      setErrorMessage("");

      // Awaited for its side effects and its throw-on-failure; the kernel's
      // `{ session, delivery, method }` is consumed by startKernelLogin, not here.
      if (isEmailMode) {
        await registerWithEmail(currentValue);
      } else {
        await registerWithPhone(countryCode, sanitizedValue, "");
      }

      // No `status === "error"` check: the kernel signals with HTTP status and axios
      // rejects non-2xx, so reaching this line already means success.
      Preferences.set({ key: "CountryCode", value: countryCode });

      // ⚠️ `token` is NOT written here any more. blaze's login/start returned
      // `{status, token}` and this wrote the OTP handle under "token"; the kernel returns
      // `{session, delivery, method}` and startKernelLogin already stashes the session
      // itself. Writing `token: undefined` made Preferences.set throw, the catch below
      // swallowed it, and the screen silently never navigated to /verify — a 200 from the
      // kernel, an OTP in the inbox, and a dead sign-in button.
      //
      // authMethod and the email/phone value ARE still required: VerifyLogin reads them to
      // resend, and verifyCode derives `loginSource` from authMethod (D20).
      const authData = {
        authMethod: isEmailMode ? "email" : "phone",
        ...(isEmailMode ? { email: currentValue } : { phone: sanitizedValue }),
      };

      await Promise.all(
        Object.entries(authData).map(([key, value]) =>
          Preferences.set({ key, value }),
        ),
      );

      router.push(
        "/verify" + (registerMode ? "?register=true" : "?register=false"),
      );
      await analytics.logEvent("login_initiated", {
        new_user: registerMode ? "true" : "false",
        aut_mode: isEmailMode ? "email" : "phone",
      });
    } catch (error: any) {
      console.log("Registration Error:", error);

      // Enhanced network error handling
      if (
        error?.message?.includes("fetch") ||
        error?.message?.includes("network") ||
        error?.message?.includes("ERR_NETWORK") ||
        error?.message?.includes("ERR_ADDRESS_UNREACHABLE") ||
        error?.name === "TypeError"
      ) {
        try {
          const currentNetworkStatus = await Network.getStatus();
          if (!currentNetworkStatus.connected) {
            setErrorMessage(
              "Connection lost. Please check your internet connection and try again.",
            );
            setShowError(true);
            return;
          }
        } catch (networkError) {
          console.error("Error checking network status:", networkError);
        }

        setErrorMessage(
          "Network error occurred. Please check your connection and try again.",
        );
        setShowError(true);
        return;
      }

      // An error carrying an HTTP status is the kernel talking, so map it rather than
      // surfacing axios's own wording -- the generic branch below would render
      // "Request failed with status code 429. Please check your email and try again.",
      // which blames the user for a server-side cooldown and hides the wait.
      if (error?.response?.status) {
        setErrorMessage(authErrorMessage(error, SEND_CODE_FAILED));
        setShowError(true);
        return;
      }

      const errorMsg = error?.message
        ? `${error.message}. Please check your ${
            isEmailMode ? "email" : "phone number"
          } and try again.`
        : "An unknown error occurred. Please try again.";

      setErrorMessage(errorMsg);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  // Social registration
  /**
   * Start social sign-in.
   *
   * ⚠️ This no longer AWAITS a session. Social now goes through Cognito Hosted UI + PKCE,
   * so the app hands off to the system browser and the session is established later, when
   * the `arcconnect://auth/callback` deep link arrives (App.tsx -> completeSocialLogin).
   * The old code awaited a userData object and branched on `response.status`/`response.error`
   * — none of which exists in this flow, and all of which would have read as failure.
   *
   * The biometric-enrolment prompt therefore cannot fire from here either; it belongs after
   * the session exists, not after the browser opens.
   */
  const handleSocialRegister = async (
    provider: "google" | "facebook" | "apple",
  ) => {
    await analytics.logLogin(provider);
    try {
      setErrorMessage("");
      setShowError(false);
      setLoading(true);
      setLoadingMessage(`Opening ${provider} sign-in...`);
      await signInWithSocial(provider);
    } catch (error: any) {
      console.error(`Error starting ${provider} sign-in:`, error);
      if (isSSOCancelError(error)) {
        setErrorMessage("The user cancelled the Sign-in-flow.");
        setShowError(true);
        return;
      }
      setErrorMessage(friendlySSOError(error, provider));
      setShowError(true);
    } finally {
      // Cleared unconditionally: the browser is now in front of the app, and a spinner left
      // running underneath is what the user returns to if they cancel.
      setLoading(false);
    }
  };

  // Biometric authentication
  const handleBiometricLogin = async () => {
    try {
      setLoadingMessage("Checking authentication options...");
      setErrorMessage("");

      const { isAvailable, biometryType } = await NativeBiometric.isAvailable();
      const biometryName =
        biometryType === 2
          ? "Face ID"
          : biometryType === 1
          ? "Touch ID"
          : "Biometric";

      if (!isAvailable) {
        setLoadingMessage("Authenticating with device passcode...");
        await NativeBiometric.verifyIdentity({
          reason: "Please authenticate to continue",
          title: "Authentication Required",
          subtitle: "Use your device passcode to log in",
          useFallback: true,
        });
      } else {
        setLoadingMessage(`Authenticating with ${biometryName}...`);
        await NativeBiometric.verifyIdentity({
          reason: "Please authenticate to continue",
          title: "Authentication Required",
          subtitle: `Use ${biometryName} to log in`,
          useFallback: true,
        });
      }

      await Preferences.set({ key: "biometric", value: "true" });
      // F-137: resume the requested screen rather than always /home (see routes/returnTo).
      router.push(takeReturnTo());
      await analytics.logEvent("biometric_login_success");
    } catch (error) {
      handleAuthError(error);
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Always render country selector when in phone mode
  const renderCountrySelector = () => {
    return (
      <div
        style={{
          position: "relative",
          width: "60px",
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #e0e0e0",
          paddingRight: "8px",
          marginRight: "8px",
        }}
        onClick={() => {
          setCodeModal(true);
        }}
      >
        <input
          style={{
            width: "100%",
            paddingRight: "20px",
            fontSize: "16px",
            border: "none",
            outline: "none",
            background: "transparent",
            cursor: "pointer",
          }}
          value={countryCode}
          readOnly
        />
        <span
          style={{
            position: "absolute",
            right: "0px",
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            fontSize: "12px",
            color: "#888",
            display: "flex",
            alignItems: "center",
          }}
        >
          ▼
        </span>
      </div>
    );
  };

  const handleAuthError = (error: any) => {
    let errorMsg = "Authentication failed";

    if (error?.code) {
      const errorMessages: { [key: number]: string } = {
        10: "Authentication failed. Please try again.",
        11: "Authentication canceled.",
        12: "Switched to device passcode.",
        13: "Authentication was canceled by the system.",
        14: "Device passcode is not set, which is required for authentication.",
      };
      errorMsg =
        errorMessages[error.code] || `Authentication error: ${error.code}`;
    }

    setErrorMessage(errorMsg);
    setShowError(true);
  };

  const getInputClassName = () => {
    const baseClass = "custom-ion-input";

    if (!inputValue.trim()) return baseClass;

    if (isEmailMode) {
      return `${baseClass} ${isEmailInput(inputValue) ? "valid" : "invalid"}`;
    } else {
      return `${baseClass} ${isInputValid() ? "valid" : ""}`;
    }
  };

  // Fixed handler for country code selection
  const handleCountryCodeSelection = (e: any) => {
    const selectedCode = e?.detail?.value || e?.target?.value;
    if (selectedCode) {
      setCountrycode(selectedCode);
      setCountryCodeManuallySet(true); // Mark as manually set
      setCodeModal(false);

      // Reformat the current input with the new country code
      if (inputValue && !isEmailMode) {
        const cleanedInput = inputValue.replace(/[^\d]/g, "");
        if (cleanedInput) {
          const formatted = formatPhoneNumber(cleanedInput, selectedCode);
          setInputValue(formatted);
          setPhoneNumbers([formatted]);
        }
      }
    }
  };

  /**
   * Terms gate, pre-OTP (D29).
   *
   * ⚠️ Gated on the DEVICE-LOCAL flag for everyone — new or returning, email or social.
   * It used to call `checkUserExists` to tell "new" from "returning" and only prompt the
   * new, but that endpoint was an unauthenticated account-existence oracle and was removed
   * (D24). The Cognito pool has "Prevent user existence errors" ENABLED, so the app was
   * re-exposing exactly what the pool is configured to hide.
   *
   * ACCEPTED UX CHANGE, explicitly signed off: a returning patient who reinstalls or moves
   * to a new phone sees the Terms screen once more before their first OTP. One extra tap,
   * on reinstall only.
   *
   * The gate cannot move to after login — the submitted TFN carrier registration shows the
   * modal firing BEFORE the OTP. The kernel's server-side consent gate (ADR-0011) is the
   * real enforcement; this is the pre-OTP display, and `agreeToTerms()` writes the
   * `termsAgreed` flag that verifyCode forwards to the kernel as `terms_accepted`.
   *
   * ⚠️ Do NOT reintroduce an existence check to spare that tap — reopen D24 instead.
   */
  const requireTermsBeforeProceed = async (action: () => void) => {
    if (!hasAgreed) {
      setPendingAction(() => action);
      setShowTermsRequiredModal(true);
      return;
    }
    action();
  };
  if (isDesktop) {
    return (
      <IonPage>
        <IonContent fullscreen className="welcome-container">
          {loading && <Loader loadingMessage={loadingMessage} />}
          <DesktopLoginLayout
            registerMode={registerMode}
            isEmailMode={isEmailMode}
            inputValue={inputValue}
            countryCode={countryCode}
            onInputChange={handleInputChange}
            onRegisterModeToggle={() => setRegisterMode(!registerMode)}
            onVisitAsGuest={async () => {
              await trackClick("clicked_visit_as_guest");
              enterGuestMode();
              router.push(GUEST_ROUTE);
            }}
            onContinue={async () =>
              await requireTermsBeforeProceed(handleRegister)
            }
            handleCountryCodeSelection={handleCountryCodeSelection}
            mappedCountryCodes={mappedCountryCodes}
            onSocialRegister={async (provider) =>
              !loading &&
              (await requireTermsBeforeProceed(() =>
                handleSocialRegister(provider),
              ))
            }
            error={showError ? errorMessage : undefined}
            loading={loading}
            hasAgreed={hasAgreed}
            onTermsAgree={async () => {
              if (!hasAgreed) {
                await agreeToTerms();
              } else {
                resetTermsAgreement();
              }
            }}
            setEmailMode={setIsEmailMode}
          />

          {/* Terms Agreement Modal (Desktop) */}
          {showTermsModal && (
            <TermsAgreementModal
              isOpen={showTermsModal}
              onClose={() => {
                setShowTermsModal(false);
                setModalContentType(null);
              }}
              conditions={modalContentType as any}
            />
          )}

          {/* Terms Required Modal (Desktop) */}
          <ActionButtons
            isOpen={showTermsRequiredModal}
            onClose={() => setShowTermsRequiredModal(false)}
            title="Agree to Terms and Privacy"
            message=""
            actionButtons={[
              {
                isPrimary: true,
                text: "I Agree",
                action: async () => {
                  await agreeToTerms();
                  setShowTermsRequiredModal(false);
                  if (pendingAction) pendingAction();
                  setPendingAction(null);
                },
              },
            ]}
          >
            <div className=" flex text-xl text-center pt-0 p-4  items-center justify-center">
              <IonLabel className="remember-label">
                You must agree to the {" "}
                <a
                  className="anchor-link"
                  onClick={() => {
                    setModalContentType("terms");
                    setShowTermsModal(true);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  terms of use
                </a>
                <span> and </span>
                <a
                  className="anchor-link"
                  onClick={() => {
                    setModalContentType("privacy");
                    setShowTermsModal(true);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  privacy policy
                </a>
                <span> to proceed.</span>
              </IonLabel>
            </div>
          </ActionButtons>
        </IonContent>
      </IonPage>
    );
  }
  return (
    <IonPage>
      <IonContent fullscreen className="welcome-container">
        {loading && <Loader loadingMessage={loadingMessage} />}

        <div className="responsive-container">
          <div className="left-section">
          </div>

          <div className="right-section">
            <IonCardContent className="card-content">
              <div className="signin-card" style={{ paddingLeft: "5px" }}>
                <h2 className="login-title">
                  {registerMode ? "Create Account" : "Sign In"}
                </h2>
              </div>

              {/* FIXED: Modern single input approach with always-visible country selector for phone mode */}
              <div
                className="custom-input-wrapper"
                style={{
                  border: "1.5px solid",
                  borderRadius: "8px",
                  padding: "6px 10px",
                  // marginBottom: "12px",
                  width: "100%",
                  // height: "46px",
                  minHeight: "46px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "2px",
                  background: "#fff",
                  position: "relative",
                  borderColor: "#DADCDF",
                }}
              >
                {/* FIXED: Always show country selector when in phone mode */}
                {!isEmailMode && renderCountrySelector()}

                {codeModal && (
                  <SelectionModal
                    isOpen={codeModal}
                    onClose={() => setCodeModal(false)}
                    title="Select Country Code"
                    options={mappedCountryCodes}
                    subtitle="Choose your country code"
                    showSearch={true}
                    fieldName="country_code"
                    buttonText="Choose"
                    onSave={handleCountryCodeSelection}
                    multiSelect={false}
                    allowCustomOptions={false}
                  />
                )}

                <input
                  value={inputValue}
                  onChange={(e) => {
                    handleInputChange(e.target.value);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !loading && isInputValid()) {
                      requireTermsBeforeProceed(handleRegister);
                    }
                  }}
                  type="text"
                  placeholder="Email or Phone Number"
                  className={getInputClassName()}
                  disabled={loading}
                  maxLength={isEmailMode ? undefined : 17}
                  style={{
                    border: "none",
                    outline: "none",
                    width: "100%",
                    borderRadius: "10px",
                    background: "transparent",
                    flex: 1,
                    fontSize: "1rem",
                    height: "46px !important",
                    padding: "2px",
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  inputMode="text"
                />

                {inputValue && (
                  <button
                    type="button"
                    className="clear-btn"
                    onClick={() => {
                      setInputValue("");
                      setEmail("");
                      setPhoneNumbers([""]);
                      setIsEmailMode(true);
                    }}
                    style={{
                      marginLeft: 8,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#888",
                      fontSize: "1.2rem",
                      padding: 0,
                    }}
                    disabled={loading}
                  >
                    &times;
                  </button>
                )}
              </div>

              {errorMessage && showError && (
                <div className="error-message">
                  <IonIcon icon={alertCircleOutline} color="danger" />
                  <IonText color="danger">{errorMessage}</IonText>
                </div>
              )}
              {!isOnline && (
                <div className="error-message">
                  <IonIcon icon={alertCircleOutline} color="danger" />
                  <IonText color="danger">
                    Please check your network Connectivity
                  </IonText>
                </div>
              )}
              <div className="m-1 mb-2 text-gray-600 font-figtree ">
                {registerMode
                  ? "Please enter your email / phone number to register."
                  : "Please enter your email / phone number to login."}
              </div>

              {registerMode && (
                <div
                  className="remember-me"
                  style={{
                    minHeight: 40,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <IonCheckbox
                    slot="start"
                    checked={hasAgreed}
                    onIonChange={async (e) => {
                      if (e.detail.checked) {
                        await agreeToTerms();
                      } else {
                        resetTermsAgreement();
                      }
                    }}
                    disabled={loading}
                  />
                  <IonLabel
                    className="remember-label"
                    style={{ fontSize: "1.1rem", cursor: "pointer" }}
                  >
                    I agree to{" "}
                    <a
                      className="anchor-link"
                      onClick={() => {
                        setModalContentType("terms");
                        setShowTermsModal(true);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      terms of use
                    </a>
                    <span> and </span>
                    <a
                      className="anchor-link"
                      onClick={() => {
                        setModalContentType("privacy");
                        setShowTermsModal(true);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      privacy policy
                    </a>
                  </IonLabel>
                </div>
              )}

              <div className="sign-in-with-faceid">
                <IonButton
                  className="sign-in-btn"
                  shape="round"
                  expand="block"
                  onClick={async () =>
                    await requireTermsBeforeProceed(handleRegister)
                  }
                  disabled={
                    loading || !isInputValid() || (registerMode && !hasAgreed) // Only require terms agreement in register mode
                  }
                >
                  {loading ? (
                    <IonSpinner name="dots" />
                  ) : (
                    `${registerMode ? "Create Account" : "Sign in"}`
                  )}
                </IonButton>
              </div>

              <div className="continue-with">
                <div className="left-line"></div>
                <span className="divider-text">Or Continue with</span>
                <div className="right-line"></div>
              </div>
              <SocialButtons
                handleSocialRegister={async (provider) =>
                  !loading &&
                  (await requireTermsBeforeProceed(() =>
                    handleSocialRegister(provider)
                  ))
                }
              />

              <div className="footer-links">
                <p
                  onClick={() => setRegisterMode(!registerMode)}
                  className="footer-text link-text"
                >
                  {registerMode ? "Already" : "Don't"} have an account?{" "}
                  <a style={{ color: "#0778CF", fontWeight: 600 }}>
                    {registerMode ? "Sign In" : "Register"}
                  </a>
                </p>
                <button
                  className="guest-link"
                  style={{ color: "#0778CF" }}
                  onClick={async () => {
                    await trackClick("clicked_visit_as_guest");
                    enterGuestMode();
                    router.push(GUEST_ROUTE);
                  }}
                >
                  Visit as a guest
                </button>
              </div>
            </IonCardContent>
          </div>
        </div>

        {/* Biometric Authentication Confirmation */}
        {showBiometricAlert && (
          <ActionButtons
            isOpen={showBiometricAlert}
            onClose={() => setShowBiometricAlert(false)}
            title="Biometric Authentication"
            message="Do you want to use biometric authentication for future logins?"
            actionButtons={[
              {
                text: "Cancel",
                action: () => {
                  setShowBiometricAlert(false);
                  // F-137: declining the biometric prompt still finishes sign-in, so it
                  // owes the user the screen they asked for.
                  router.push(takeReturnTo());
                },
              },
              {
                isPrimary: true,
                text: "Confirm",
                action: () => {
                  handleBiometricLogin();
                },
              },
            ]}
          />
        )}

        {/* Terms Agreement Modal */}
        {showTermsModal && (
          <TermsAgreementModal
            isOpen={showTermsModal}
            onClose={() => {
              setShowTermsModal(false);
              setModalContentType(null);
            }}
            conditions={modalContentType as any}
          />
        )}

        {showTermsRequiredModal && (
          <ActionButtons
            isOpen={showTermsRequiredModal}
            onClose={() => setShowTermsRequiredModal(false)}
            title="Agree to Terms and Privacy"
            message=""
            actionButtons={[
              {
                isPrimary: true,
                text: "I Agree",
                action: async () => {
                  await agreeToTerms();
                  setShowTermsRequiredModal(false);
                  if (pendingAction) pendingAction();
                  setPendingAction(null);
                },
              },
            ]}
          >
          <div className=" flex text-xl text-center pt-0 p-4  items-center justify-center">
            {/* You must agree to the Terms of use and privacy policy to proceed. */}
            <IonLabel className="remember-label">
              You must agree to the{" "}
              <a
                className="anchor-link"
                onClick={() => {
                  setModalContentType("terms");
                  setShowTermsModal(true);
                }}
                style={{ cursor: "pointer" }}
              >
                terms of use
              </a>
              <span> and </span>
              <a
                className="anchor-link"
                onClick={() => {
                  setModalContentType("privacy");
                  setShowTermsModal(true);
                }}
                style={{ cursor: "pointer" }}
              >
                privacy policy
              </a>
              <span> to proceed.</span>
            </IonLabel>
          </div>
        </ActionButtons>
        )}
      </IonContent>
    </IonPage>
  );
};

export default Register;
